import * as React from 'react';
const { useCallback, useMemo, useRef } = React;
import { useLazyWavesurfer } from '@/hooks/useLazyWavesurfer';
import { WaveForm, type GetCommentsQueryData } from '@/generated/graphql';
import {
  CommentMarkers,
  useElementWidth,
} from '@/common/components/waveformComments/ReviewWaveform';
import {
  decodeWaveformPeaks,
  formatTime,
} from '@/common/utils/audioPlayerUtils';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import {
  AudioPreviewSurface,
  PlaybackButton,
} from '@/common/components/ui/LibraryPrimitives';

interface AudioPlayerProps {
  waveForm?: WaveForm | null;
  projectId?: string;
  resolveAudioSource?: () => Promise<string | null>;
  fileId: string;
  variant?: 'default' | 'inline' | 'banner';
  label?: string;
  tabIndex?: number;
  /** Open top-level comments to mark in the banner lane (and count). */
  commentThreads?: GetCommentsQueryData['comments'];
  /** When provided, show a comments count button that calls this. */
  onOpenComments?: (threadId?: string, atMs?: number) => void;
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  waveForm,
  projectId,
  resolveAudioSource,
  fileId,
  variant = 'default',
  label = 'audio preview',
  tabIndex,
  commentThreads,
  onOpenComments,
}) => {
  const theme = useTheme();
  const isInline = variant === 'inline';
  const isBanner = variant === 'banner';
  const encodedPeaks = waveForm?.peaks;
  const peaks = useMemo(
    () => (encodedPeaks ? decodeWaveformPeaks(encodedPeaks) : undefined),
    [encodedPeaks]
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const { ref: markerLaneRef, width: markerLaneWidth } =
    useElementWidth<HTMLDivElement>();
  const markers = useMemo(
    () =>
      (waveForm?.duration ?? 0) > 0
        ? (commentThreads ?? []).filter((t) => !t.resolvedAt && t.anchor)
        : [],
    [commentThreads, waveForm?.duration]
  );
  // Depends on the thread's own anchor, not the playhead, so timeupdate
  // renders keep it stable.
  const onMarkerClick = useCallback(
    (id: string) =>
      onOpenComments?.(id, markers.find((t) => t._id === id)?.anchor?.atMs),
    [markers, onOpenComments]
  );
  const {
    wavesurfer,
    isPlaying,
    currentTime,
    isLoadingAudioSource,
    onPlayPause,
    cancelAutoPlay,
  } = useLazyWavesurfer({
    projectId,
    fileId,
    resolveAudioSource,
    containerRef,
    options: {
      height: isBanner ? 88 : isInline ? 26 : 32,
      peaks: peaks,
      duration: waveForm?.duration,
      waveColor: alpha(theme.palette.text.secondary, isInline ? 0.5 : 0.35),
      progressColor: theme.palette.primary.main,
      cursorColor: isInline
        ? 'transparent'
        : alpha(theme.palette.primary.light, 0.8),
      cursorWidth: 1,
      dragToSeek: true,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      normalize: true,
    },
  });

  return (
    <AudioPreviewSurface
      playing={isPlaying}
      data-cy={`audio-player-${fileId}`}
      sx={
        isBanner
          ? {
              height: 'auto',
              minHeight: 244,
              display: 'grid',
              gridTemplateColumns: onOpenComments
                ? '44px minmax(0, 1fr) auto'
                : '44px minmax(0, 1fr)',
              p: 0,
              columnGap: 2,
              rowGap: 1.5,
              border: 0,
              borderRadius: 0,
              bgcolor: 'transparent',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }
          : undefined
      }
    >
      <PlaybackButton
        playing={isPlaying}
        size="small"
        disabled={isLoadingAudioSource}
        aria-label={`${isLoadingAudioSource ? 'Loading' : isPlaying ? 'Pause' : 'Play'} ${label}`}
        tabIndex={tabIndex}
        sx={
          isBanner
            ? { gridRow: 1, width: 44, height: 44, '& svg': { fontSize: 24 } }
            : undefined
        }
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            void onPlayPause();
          }
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onPlayPause();
        }}
      >
        {isLoadingAudioSource ? (
          <CircularProgress
            data-cy={`audio-loading-${fileId}`}
            size={13}
            color="inherit"
          />
        ) : isPlaying ? (
          <PauseIcon data-cy={`pause-icon-${fileId}`} sx={{ fontSize: 16 }} />
        ) : (
          <PlayArrowIcon
            data-cy={`play-icon-${fileId}`}
            sx={{ fontSize: 16 }}
          />
        )}
      </PlaybackButton>

      {/* Waveform mount point */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          ...(isBanner && { gridColumn: '1 / -1', gridRow: 2 }),
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <div
            ref={containerRef}
            style={{ width: '100%' }}
            data-cy={`audio-player-waveform-${fileId}`}
          />
        </Box>
      </Box>

      {isBanner && (
        <Box
          data-testid="preview-comment-lane"
          sx={{ gridColumn: '1 / -1', gridRow: 3, alignSelf: 'stretch' }}
        >
          <Box
            aria-label="Preview timeline"
            sx={{
              position: 'relative',
              height: 28,
              borderTop: 1,
              borderColor: 'divider',
              pt: 1,
            }}
          >
            {[0, 0.25, 0.5, 0.75, 1].map((fraction) => (
              <Typography
                key={fraction}
                variant="caption"
                color="text.secondary"
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: 10,
                  position: 'absolute',
                  left: `${fraction * 100}%`,
                  transform:
                    fraction === 0
                      ? undefined
                      : fraction === 1
                        ? 'translateX(-100%)'
                        : 'translateX(-50%)',
                }}
              >
                {formatTime(
                  (waveForm?.duration ?? wavesurfer?.getDuration() ?? 0) *
                    fraction
                )}
              </Typography>
            ))}
          </Box>
          <Box
            ref={markerLaneRef}
            sx={{
              position: 'relative',
              minHeight: 96,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            {markers.length > 0 && (
              <CommentMarkers
                threads={markers}
                durationMs={(waveForm?.duration ?? 0) * 1000}
                width={markerLaneWidth}
                onMarkerClick={onMarkerClick}
              />
            )}
          </Box>
        </Box>
      )}

      <Typography
        variant="caption"
        sx={{
          fontSize: isBanner ? '12px' : '10px',
          color: isPlaying ? theme.palette.primary.main : 'text.secondary',
          flexShrink: 0,
          minWidth: 32,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: isPlaying ? 600 : 400,
          ...(isBanner && { gridColumn: 2, gridRow: 1 }),
        }}
      >
        {formatTime(
          isInline && currentTime === 0 && !isPlaying
            ? (waveForm?.duration ?? wavesurfer?.getDuration() ?? 0)
            : currentTime
        )}
        {isBanner &&
          ` / ${formatTime(waveForm?.duration ?? wavesurfer?.getDuration() ?? 0)}`}
      </Typography>

      {onOpenComments && (
        <Tooltip title="Open comments">
          <IconButton
            size="small"
            data-cy={`open-comments-${fileId}`}
            aria-label={`${markers.length} open ${markers.length === 1 ? 'comment' : 'comments'}`}
            sx={{
              flexShrink: 0,
              gap: 0.5,
              minWidth: 32,
              minHeight: 32,
              fontSize: '12px',
              p: 0.5,
              ...(isBanner && { gridColumn: 3, gridRow: 1 }),
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // The comments dialog has its own player; avoid two at once,
              // and don't let a pending autoplay start underneath it.
              if (isPlaying) void onPlayPause();
              cancelAutoPlay();
              onOpenComments(undefined, currentTime * 1000);
            }}
          >
            <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />
            <Typography
              component="span"
              variant="caption"
              sx={{ fontSize: 'inherit', fontVariantNumeric: 'tabular-nums' }}
            >
              {markers.length}
            </Typography>
          </IconButton>
        </Tooltip>
      )}
    </AudioPreviewSurface>
  );
};
